import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail, ArrowUpRight } from 'lucide-react';

import Logo from './Logo';

const LinkList = ({
  heading,
  items,
}: {
  heading: string;
  items: { label: string; to?: string; href?: string }[];
}) => (
  <div>
    <p className="eyebrow mb-5">{heading}</p>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.label}>
          {item.to ? (
            <Link
              to={item.to}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {item.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer className="relative border-t border-white/5 mt-16">
    <div className="container-custom py-16 md:py-20">
      {/* Top row — brand + live pill */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-12 border-b border-white/5">
        <Link to="/" className="inline-flex" aria-label="Emerge Automations, Home">
          <Logo className="h-9" />
        </Link>
        <div className="flex items-center gap-3">
          <span className="live-pill">
            <span className="live-pill__dot" />
            <span>24/7 automations live</span>
          </span>
          <Link
            to="/#calendly-booking"
            className="btn-secondary"
          >
            Book audit
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </div>

      {/* Columns */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8 py-12">
        <div className="col-span-2 md:col-span-4">
          <p className="text-[15px] text-zinc-400 max-w-sm leading-relaxed">
            Helping SMEs use the next generation of artificial intelligence. Based in London, serving the world.
          </p>
          <div className="flex gap-2 mt-6">
            <a
              href="https://linkedin.com/company/emergeautomations"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-[var(--radius-sm)] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
              aria-label="Emerge Automations on LinkedIn"
            >
              <Linkedin className="w-4 h-4" strokeWidth={1.75} />
            </a>
            <a
              href="https://twitter.com/emergeautomations"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-[var(--radius-sm)] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
              aria-label="Emerge Automations on Twitter"
            >
              <Twitter className="w-4 h-4" strokeWidth={1.75} />
            </a>
            <a
              href="mailto:emergeautomations@gmail.com"
              className="w-9 h-9 rounded-[var(--radius-sm)] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
              aria-label="Email Emerge Automations"
            >
              <Mail className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <LinkList
            heading="Services"
            items={[
              { label: 'AI Voice Agents', to: '/services/ai-voice-agents/' },
              { label: 'Workflow Automation', to: '/services/n8n-workflow-automation/' },
              { label: 'Custom AI Assistants', to: '/services/custom-llm-training/' },
              { label: 'Smart Chatbots', to: '/services/intelligent-chatbots/' },
              { label: 'AI Strategy', to: '/services/strategic-ai-consulting/' },
              { label: 'Process Optimization', to: '/services/process-optimization/' },
            ]}
          />
        </div>

        <div className="md:col-span-2">
          <LinkList
            heading="Resources"
            items={[
              { label: 'Blog & Insights', to: '/blog/' },
              { label: 'Privacy Policy', to: '/privacy-policy/' },
              { label: 'Terms of Service', to: '/terms-of-service/' },
            ]}
          />
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow mb-5">Contact</p>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li>
              <a href="mailto:emergeautomations@gmail.com" className="hover:text-white transition-colors">
                emergeautomations@gmail.com
              </a>
            </li>
            <li>London, United Kingdom</li>
            <li className="pt-2">
              <Link
                to="/#calendly-booking"
                className="inline-flex items-center gap-1.5 text-[color:var(--color-accent)] font-medium hover:gap-2 transition-all"
              >
                Book a free AI audit
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom row */}
      <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
        <span>&copy; 2026 Emerge Automations. All rights reserved.</span>
        <span data-mono className="tracking-[0.08em] uppercase">
          ver · 2026.01 · london
        </span>
      </div>
    </div>
  </footer>
);

export default Footer;
